import React, { useState, useEffect, useCallback } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { 
  Users, Search, Filter, Download, RefreshCw, 
  Mail, Phone, MapPin, Calendar, ShoppingBag, 
  DollarSign, CheckCircle, XCircle, ChevronLeft, ChevronRight,
  TrendingUp, Gift, BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const AdminCustomers = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    search: '',
    city: '',
    coupon_used: '',
    has_purchased: '',
    date_from: '',
    date_to: ''
  });
  const [cities, setCities] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      // Build query params
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', 25);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.city) params.append('city', filters.city);
      if (filters.coupon_used !== '') params.append('coupon_used', filters.coupon_used);
      if (filters.has_purchased !== '') params.append('has_purchased', filters.has_purchased);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await fetchWithAuth(`/api/admin/customers?${params.toString()}`);
      const data = await response.json();
      
      setCustomers(data.customers || []);
      setTotalPages(data.pages || 1);
      setTotal(data.total || 0);
      setCities(data.cities || []);
    } catch (error) {
      toast.error('Error al cargar clientes');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, page, filters]);

  const loadStats = useCallback(async () => {
    try {
      const response = await fetchWithAuth('/api/admin/customers/stats');
      const data = await response.json();
      setStats(data);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  }, [fetchWithAuth]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPage(1); // Reset to first page
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      city: '',
      coupon_used: '',
      has_purchased: '',
      date_from: '',
      date_to: ''
    });
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filters.city) params.append('city', filters.city);
      if (filters.coupon_used !== '') params.append('coupon_used', filters.coupon_used);
      if (filters.has_purchased !== '') params.append('has_purchased', filters.has_purchased);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      
      const response = await fetchWithAuth(`/api/admin/customers/export?${params.toString()}`);
      
      // Get the blob and download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clientes_alphatica_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      
      toast.success('Exportación completada');
    } catch (error) {
      toast.error('Error al exportar');
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount || 0);
  };

  return (
    <div className="p-6" data-testid="admin-customers-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <Users className="text-neon-cyan" />
            Clientes Registrados
          </h1>
          <p className="text-gray-400 text-sm mt-1">
            {total} clientes en total
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadCustomers()}
            className="p-2 text-gray-400 hover:text-white transition-colors"
            title="Actualizar"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              showFilters ? 'bg-neon-cyan/20 text-neon-cyan' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Filter size={18} />
            Filtros
          </button>
          
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
            data-testid="export-csv-btn"
          >
            <Download size={18} />
            {exporting ? 'Exportando...' : 'Exportar CSV'}
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                <Users size={20} className="text-blue-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{stats.total_customers}</p>
            <p className="text-xs text-gray-500">Total Registrados</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                <Gift size={20} className="text-green-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{stats.coupon_used}</p>
            <p className="text-xs text-gray-500">Cupón Usado</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500/20 rounded-lg flex items-center justify-center">
                <Gift size={20} className="text-amber-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{stats.coupon_unused}</p>
            <p className="text-xs text-gray-500">Cupón Sin Usar</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-purple-500/20 rounded-lg flex items-center justify-center">
                <ShoppingBag size={20} className="text-purple-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-white">{stats.purchased_registered}</p>
            <p className="text-xs text-gray-500">Han Comprado</p>
          </div>
          
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-neon-pink/20 rounded-lg flex items-center justify-center">
                <TrendingUp size={20} className="text-neon-pink" />
              </div>
            </div>
            <p className="text-2xl font-bold text-neon-pink">{stats.conversion_rate}%</p>
            <p className="text-xs text-gray-500">Tasa Conversión</p>
          </div>
        </div>
      )}

      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-gray-800 rounded-xl p-4 mb-6 border border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Buscar</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  name="search"
                  value={filters.search}
                  onChange={handleFilterChange}
                  placeholder="Nombre, email, tel..."
                  className="w-full pl-9 pr-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-neon-cyan"
                  data-testid="filter-search"
                />
              </div>
            </div>
            
            {/* City */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ciudad</label>
              <select
                name="city"
                value={filters.city}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-neon-cyan"
                data-testid="filter-city"
              >
                <option value="">Todas</option>
                {cities.map(city => (
                  <option key={city} value={city}>{city}</option>
                ))}
              </select>
            </div>
            
            {/* Coupon Used */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Cupón Usado</label>
              <select
                name="coupon_used"
                value={filters.coupon_used}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-neon-cyan"
                data-testid="filter-coupon"
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            
            {/* Has Purchased */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Ha Comprado</label>
              <select
                name="has_purchased"
                value={filters.has_purchased}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-neon-cyan"
                data-testid="filter-purchased"
              >
                <option value="">Todos</option>
                <option value="true">Sí</option>
                <option value="false">No</option>
              </select>
            </div>
            
            {/* Date From */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Desde</label>
              <input
                type="date"
                name="date_from"
                value={filters.date_from}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-neon-cyan"
                data-testid="filter-date-from"
              />
            </div>
            
            {/* Date To */}
            <div>
              <label className="block text-xs text-gray-500 mb-1">Hasta</label>
              <input
                type="date"
                name="date_to"
                value={filters.date_to}
                onChange={handleFilterChange}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white text-sm focus:outline-none focus:border-neon-cyan"
                data-testid="filter-date-to"
              />
            </div>
          </div>
          
          <div className="mt-4 flex justify-end">
            <button
              onClick={clearFilters}
              className="text-sm text-gray-400 hover:text-white transition-colors"
            >
              Limpiar filtros
            </button>
          </div>
        </div>
      )}

      {/* Customers Table */}
      <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center">
            <RefreshCw size={32} className="animate-spin text-neon-cyan mx-auto mb-4" />
            <p className="text-gray-400">Cargando clientes...</p>
          </div>
        ) : customers.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={48} className="text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400">No hay clientes registrados</p>
            <p className="text-gray-500 text-sm mt-1">Los clientes aparecerán aquí cuando se registren</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Cliente</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Contacto</th>
                    <th className="text-left px-4 py-3 text-xs text-gray-500 uppercase">Ciudad</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Cupón</th>
                    <th className="text-center px-4 py-3 text-xs text-gray-500 uppercase">Compras</th>
                    <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Total Gastado</th>
                    <th className="text-right px-4 py-3 text-xs text-gray-500 uppercase">Registro</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {customers.map((customer) => (
                    <tr key={customer.id} className="hover:bg-gray-700/50 transition-colors">
                      <td className="px-4 py-3">
                        <p className="font-medium text-white">{customer.name}</p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="space-y-1">
                          <p className="text-sm text-gray-300 flex items-center gap-1">
                            <Mail size={12} className="text-gray-500" />
                            {customer.email}
                          </p>
                          <p className="text-sm text-gray-400 flex items-center gap-1">
                            <Phone size={12} className="text-gray-500" />
                            {customer.phone}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1 text-sm text-gray-300">
                          <MapPin size={12} className="text-gray-500" />
                          {customer.city}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        {customer.coupon_used ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs">
                            <CheckCircle size={12} />
                            Usado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-600/50 text-gray-400 rounded-full text-xs">
                            <XCircle size={12} />
                            Sin usar
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                          customer.total_orders > 0 
                            ? 'bg-purple-500/20 text-purple-400' 
                            : 'bg-gray-600/50 text-gray-400'
                        }`}>
                          <ShoppingBag size={12} />
                          {customer.total_orders}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-medium ${
                          customer.total_spent > 0 ? 'text-neon-cyan' : 'text-gray-500'
                        }`}>
                          {formatCurrency(customer.total_spent)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm text-gray-400">
                          {formatDate(customer.registered_at)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
                <p className="text-sm text-gray-500">
                  Página {page} de {totalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="p-2 text-gray-400 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRight size={20} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminCustomers;
